import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, Subscription, switchMap, map } from 'rxjs';
import { CreateMonitoringBriefDto, ScreeningListCreatorItem } from '@/app/common/general-types/social-screening.dto';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { FirebaseService } from '@/app/common/firebase/firebase.service';
import { SocialMonitoring } from '@/app/services/social-monitoring/social-monitoring';
import { RecruitGroupService } from '@/app/services/recruit-group/recruit-group.service';
import { CreatorService } from '@/app/services/creator/creator.service';
import { AlertService } from '@/app/atoms/alert/alert.service';
import { ScreeningOverlay } from '@/app/pages/screening-brief-select/overlays/screening-overlay/screening-overlay';
import { CreateMonitoring } from './services/create-monitoring';
import { MONITORING_SUBMIT_STAGES } from './monitoring-brief.constants';
import { FlowChooserMonitoring, MonitoringFlow } from './flows/flow-chooser-monitoring/flow-chooser-monitoring';
import { WizardFirstMonitoring } from './flows/wizard-first-monitoring/wizard-first-monitoring';
import { AiFirstMonitoring } from './flows/ai-first-monitoring/ai-first-monitoring';

type Screen = 'chooser' | 'ai' | 'wizard';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

@Component({
  selector: 'app-create-monitoring-brief',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FlowChooserMonitoring, AiFirstMonitoring, WizardFirstMonitoring, ScreeningOverlay],
  templateUrl: './create-monitoring-brief.html',
  styleUrl: './create-monitoring-brief.scss',
})
export class CreateMonitoringBrief implements OnInit, OnDestroy {

  /** Services */
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  public readonly createMonitoringService = inject(CreateMonitoring);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly firebaseService = inject(FirebaseService);
  private readonly socialMonitoringService = inject(SocialMonitoring);
  private readonly recruitGroupService = inject(RecruitGroupService);
  private readonly creatorService = inject(CreatorService);
  private readonly alertService = inject(AlertService);
  /** Services - END */

  readonly screen = signal<Screen>('chooser');
  readonly routeType = signal<string | undefined>(undefined);

  readonly submitStages = MONITORING_SUBMIT_STAGES;

  /** `idle` hides the overlay; `sending` spins; `done` shows the submitted state. */
  readonly submitStage = signal<'idle' | 'sending' | 'done'>('idle');
  readonly submittedBriefId = signal<string | undefined>(undefined);

  readonly briefName = computed(
    () => this.createMonitoringService.basicInfoModel().briefTitle.trim() || 'Your monitoring brief',
  );

  readonly overlayResult = computed<'running' | 'pass'>(
    () => (this.submitStage() === 'done' ? 'pass' : 'running'),
  );

  readonly monitoringTypeLabel = computed(() => {
    const match = this.createMonitoringService.monitoringTypeOptions.find(
      (option) => option.type === this.createMonitoringService.monitorType(),
    );
    return match?.name ?? 'Monitoring';
  });

  /** The closing line of the submitted overlay, which depends on how creators were sourced. */
  readonly creatorLine = computed<string>(() => {
    if (this.createMonitoringService.creatorSourceMethod() === 'later') {
      return 'You can add creators any time from Manage Brief.';
    }
    if (this.createMonitoringService.creatorSourceMethod() === 'csv') {
      return this.createMonitoringService.uploadedCsvUrl()
        ? 'Creators from your file are being added to this brief.'
        : 'No creators added yet — add them from Manage Brief.';
    }
    const queued = this.queuedCreatorCount();
    if (!queued) {
      return 'No creators added yet — add them from Manage Brief.';
    }
    return `${queued} ${queued === 1 ? 'creator is' : 'creators are'} queued for monitoring.`;
  });

  readonly submittedSub = computed(() => `is live. ${this.creatorLine()}`);

  private readonly subscriptionList = new Subscription();

  ngOnInit(): void {
    this.subscriptionList.add(
      this.route.params.subscribe((params) => {
        this.routeType.set(params['type']);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
    this.createMonitoringService.reset();
    this.submitStage.set('idle');
    this.screen.set('chooser');
  }

  chooseFlow(flow: MonitoringFlow): void {
    this.screen.set(flow);
  }

  switchFlow(): void {
    this.screen.set('chooser');
  }

  /** Clears the AI draft so the user can start the AI-first flow again from scratch. */
  startOver(): void {
    this.createMonitoringService.reset();
  }

  exit(): void {
    this.location.back();
  }

  submit(): void {
    if (this.submitStage() !== 'idle') {
      return;
    }
    this.submitStage.set('sending');

    this.subscriptionList.add(
      this.resolveRecruitGroupId().pipe(
        switchMap((recruitGroupId) => {
          const brief = this.buildBrief(recruitGroupId);
          return this.socialMonitoringService.createMonitoringBrief(brief).pipe(
            switchMap((response) => this.importCsvIfNeeded(response)),
          );
        }),
      ).subscribe({
        next: (response: unknown) => {
          this.submittedBriefId.set(this.readId(response));
          this.submitStage.set('done');
        },
        error: (error: unknown) => {
          console.error('Error creating monitoring brief: ', error);
          this.submitStage.set('idle');
          const message = error instanceof Error && error.message
            ? error.message
            : 'We couldn\'t create this monitoring brief. Please try again.';
          void this.alertService.alert({
            title: 'Couldn\'t launch brief',
            message,
            type: 'alert',
            icon: 'error',
            confirmText: 'OK',
            cancelText: 'Close',
          });
        },
      }),
    );
  }

  openManageBrief(): void {
    const briefId = this.submittedBriefId();
    this.submitStage.set('idle');
    if (briefId) {
      void this.router.navigate(['/manage-brief-monitor', briefId]);
    } else {
      this.location.back();
    }
  }

  goHome(): void {
    this.submitStage.set('idle');
    this.location.back();
  }

  /**
   * Calendar start sent as `monitorStart`.
   * A selected date is posted as-is. Anything else falls back to 30 days ago.
   */
  getMonitoringStart(): Date {
    const selected = this.createMonitoringService.monitorStart();
    return selected ?? new Date(Date.now() - THIRTY_DAYS_MS);
  }

  /**
   * Calendar end sent as `monitorUntil`.
   * A selected date is posted as-is. Anything else falls back to 30 days from now.
   */
  getMonitoringUntil(): Date {
    const selected = this.createMonitoringService.monitorUntil();
    return selected ?? new Date(Date.now() + THIRTY_DAYS_MS);
  }

  buildBrief(recruitGroupId: string | undefined): CreateMonitoringBriefDto {
    return {
      briefTitle: this.createMonitoringService.basicInfoModel().briefTitle,
      businessAccountId: this.currentUserService.focussedBrand.id,
      briefUrl: this.createMonitoringService.requirementsDocumentUrl(),
      contentRequirements: this.createMonitoringService.requirementsHtml(),
      contentDonts: this.createMonitoringService.restrictionsHtml(),
      briefText: this.createMonitoringService.briefTextHtml(),
      productId: this.createMonitoringService.selectedProduct()?.id,
      monitorStart: this.getMonitoringStart(),
      monitorUntil: this.getMonitoringUntil(),
      recruitGroupId,
      monitorType: this.createMonitoringService.monitorType(),
      publishedOnly: this.createMonitoringService.publishedOnly(),
      commentAnalysis: this.createMonitoringService.commentAnalysis(),
      complianceDocIds: this.createMonitoringService.selectedComplianceIds(),
    };
  }

  /**
   * The recruit group this brief should use.
   *
   * Returns undefined when nothing was staged — the API then mints a brief-scoped group,
   * and the brief waits at Pre_Creators until creators are added.
   */
  resolveRecruitGroupId(): Observable<string | undefined> {
    const method = this.createMonitoringService.creatorSourceMethod();

    if (method === 'shout') {
      return of(this.createMonitoringService.selectedList()?.id);
    }

    if (method === 'one') {
      const manualCreators = this.createMonitoringService.manualCreators();
      if (manualCreators.length) {
        return this.createListFrom(manualCreators, 'Creators added by hand when this brief was created.');
      }
    }

    return of(undefined);
  }

  private queuedCreatorCount(): number {
    const manual = this.createMonitoringService.manualCreators().length;
    if (manual) {
      return manual;
    }
    const list = this.createMonitoringService.selectedList();
    return list?.numberOfCreators ?? list?.creators?.length ?? 0;
  }

  /**
   * Creates the recruit group a brief needs when creators were staged in this flow, then
   * adds those creators to it. The group is synced to Noodle so it owns the creator list.
   */
  private createListFrom(
    creators: ScreeningListCreatorItem[],
    listDescription: string,
  ): Observable<string | undefined> {
    const briefTitle = this.createMonitoringService.basicInfoModel().briefTitle.trim();
    const creatorIds = creators
      .map((item) => item.blazeFirebaseUid ?? item.creatorId)
      .filter((id): id is string => !!id);
    const handleRows = creators
      .filter((item) => !(item.blazeFirebaseUid ?? item.creatorId))
      .map(({ name, instagramHandle, tiktokHandle }) => ({ name, instagramHandle, tiktokHandle }));

    return this.recruitGroupService.createRecruitGroup({
      groupName: briefTitle ? `${briefTitle} creators` : 'Selected creators',
      groupDescription: listDescription,
      businessAccountId: this.currentUserService.focussedBrand.id,
      createdBy: this.firebaseService.userId ?? '',
      isGlobal: false,
      syncWithNoodle: true,
    }).pipe(
      switchMap((group) => {
        const groupId = this.readId(group);
        if (!groupId || (creatorIds.length === 0 && handleRows.length === 0)) {
          return of(groupId);
        }
        return this.recruitGroupService
          .addCreatorsToGroup(groupId, creatorIds, handleRows)
          .pipe(
            map((result) => {
              if (result.added.length + result.alreadyPresent.length === 0) {
                throw new Error(this.noCreatorsAddedMessage(result.failedCreators));
              }
              return groupId;
            }),
          );
      }),
    );
  }

  /**
   * CSV rows join the brief's group after it exists. Monitoring create has no csv_link field,
   * so the existing importer adds them through createInquiry onto that same group.
   */
  private importCsvIfNeeded(response: unknown): Observable<unknown> {
    const csvUrl = this.createMonitoringService.uploadedCsvUrl();
    const briefId = this.readId(response);
    if (this.createMonitoringService.creatorSourceMethod() !== 'csv' || !csvUrl || !briefId) {
      return of(response);
    }
    return this.creatorService.uploadCreatorsCsvMonitoring(csvUrl, briefId).pipe(
      map(() => response),
    );
  }

  private noCreatorsAddedMessage(failed: { name: string; message: string }[] | undefined): string {
    const first = failed?.[0];
    return first
      ? `We couldn't add ${first.name || 'your creators'} to this brief: ${first.message}.`
      : 'None of these creators could be added to this brief. Each needs an Instagram or TikTok handle.';
  }

  private readId(response: unknown): string | undefined {
    if (typeof response !== 'object' || response === null || !('id' in response)) {
      return undefined;
    }
    const id = (response as { id: unknown }).id;
    return typeof id === 'string' ? id : undefined;
  }
}
